// What the operator reads after pressing "Fill all".
//
// ⚠️ Until 2026-09-21 the backend filled EVERY configured position or none at all, so a 200 meant
// the storage was full and the screen could safely say so. Since the fix decided by the PO on
// 2026-09-17 it fills as many positions as there is stock for, so a 200 can mean 3 of 20 -- and the
// old wording would tell the operator the storage is full when it is not. On the shop floor the
// alert is the only thing that gets read, so the wording is the whole fix.
//
// Sun reports the two counts in the fillAll body: `filledPositions` and `requestedPositions`.
// The denominator counts the positions that EXIST in the shelf, not the configuration rows: a
// configured position that was deleted from the shelf cannot be filled, so it does not count. That
// is why this number can be smaller than what the configuration screen shows, and that is correct.
//
// ⚠️ The counts can be missing -- a backend from before they shipped -- and the message still has to
// be honest without them. It then says the available parts were placed and claims nothing about
// being full, which is true either way.

// No sentinel of "unknown" can be a real count, so a negative one cannot collide with a reading.
const COUNT_UNKNOWN = -1

const countOf = (value) => (Number.isInteger(value) && value >= 0 ? value : COUNT_UNKNOWN)

/**
 * Decides title, message and severity for the alert shown after a fill-all.
 *
 * @param {Object} response Body of POST shelveStoragePositions/fillAll
 * @returns {{titleKey: string, messageKey: string, values: Object, severity: string,
 *            complete: boolean}} Keys to translate, values to interpolate, and whether the
 *   storage ended up full. `complete` is false whenever that cannot be asserted.
 */
const storageFillOutcome = (response = {}) => {
  const filled = countOf(response.filledPositions)
  const requested = countOf(response.requestedPositions)

  // Without both counts, or with nothing to fill, the only honest thing left is that whatever was
  // available was placed. Never claim the storage is full from a bare 200 again.
  if (filled === COUNT_UNKNOWN || requested === COUNT_UNKNOWN || requested === 0) {
    return {
      titleKey: 'Success',
      messageKey: 'Storage has been filled with the parts that were available',
      values: {},
      severity: 'success',
      complete: false
    }
  }

  if (filled === 0) {
    return {
      titleKey: 'Storage not completely filled',
      messageKey: 'No position could be filled: there were no parts available',
      values: {},
      severity: 'warning',
      complete: false
    }
  }

  // A backend that reports more filled than requested is still a full storage, not a failure.
  if (filled >= requested) {
    return {
      titleKey: 'Success',
      messageKey: 'Storage has been succesfully filled',
      values: {},
      severity: 'success',
      complete: true
    }
  }

  return {
    titleKey: 'Storage not completely filled',
    messageKey: '{{filled}} of {{requested}} positions have been filled',
    values: { filled, requested },
    severity: 'warning',
    complete: false
  }
}

export { storageFillOutcome, COUNT_UNKNOWN }
